import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  getDateOptions,
  isSpecialDay,
  isLeapYear,
  addDays,
  daysBetween,
  getNotableEvent
} from '../lib/harptos'
import './ToolsPage.css'

function HarptosDateInput({ prefix, month, day, year, onChange }) {
  const dateOptions = getDateOptions()
  const isSpecial = isSpecialDay(month)

  return (
    <div className="harptos-date-input">
      <div className="form-group">
        <label>Month / Festival</label>
        <select
          value={month}
          onChange={(e) => onChange({ month: e.target.value, day: isSpecialDay(e.target.value) ? null : day, year })}
        >
          {dateOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.isSpecial ? `★ ${opt.label}` : opt.label}
            </option>
          ))}
        </select>
      </div>
      <div className="date-row">
        <div className="form-group">
          <label>Day</label>
          <input
            type="number"
            min="1"
            max="30"
            value={isSpecial ? '' : (day || '')}
            disabled={isSpecial}
            onChange={(e) => onChange({ month, day: parseInt(e.target.value) || 1, year })}
          />
        </div>
        <div className="form-group">
          <label>Year (DR)</label>
          <input
            type="number"
            value={year}
            onChange={(e) => onChange({ month, day, year: parseInt(e.target.value) || 1492 })}
          />
        </div>
      </div>
    </div>
  )
}

function DateCalculator() {
  const [startDate, setStartDate] = useState({ month: 'Hammer', day: 1, year: 1492 })
  const [daysToAdd, setDaysToAdd] = useState(30)
  const [result, setResult] = useState(null)
  const [error, setError] = useState(null)

  const calculate = () => {
    setError(null)
    setResult(null)

    const isSpecial = isSpecialDay(startDate.month)

    if (!isSpecial && (startDate.day < 1 || startDate.day > 30)) {
      setError('Day must be between 1 and 30')
      return
    }

    if (startDate.month === 'Shieldmeet' && !isLeapYear(startDate.year)) {
      setError(`${startDate.year} DR is not a leap year — Shieldmeet does not occur`)
      return
    }

    const days = typeof daysToAdd === 'number' ? daysToAdd : 0
    const newDate = addDays(startDate.month, startDate.day, startDate.year, days)
    const notable = getNotableEvent(newDate.month, newDate.day)
    setResult({ ...newDate, notable })
  }

  const useAsStartDate = () => {
    if (result) {
      setStartDate({
        month: result.month,
        day: result.day,
        year: result.year
      })
      setResult(null)
    }
  }

  return (
    <section className="tool-card">
      <h2>Date Calculator</h2>
      <p className="tool-description">Add or subtract days from a Harptos date</p>

      <div className="date-section">
        <h3>Starting Date</h3>
        <HarptosDateInput
          prefix="start"
          month={startDate.month}
          day={startDate.day}
          year={startDate.year}
          onChange={setStartDate}
        />
      </div>

      <div className="form-group">
        <label>Days to Add (negative to subtract)</label>
        <input
          type="number"
          value={daysToAdd}
          onChange={(e) => {
            const val = e.target.value
            if (val === '' || val === '-') {
              setDaysToAdd(val)
            } else {
              setDaysToAdd(parseInt(val, 10))
            }
          }}
          className="days-input"
        />
      </div>

      <button className="btn btn-primary" onClick={calculate}>
        Calculate
      </button>

      {error && <div className="tool-error">{error}</div>}

      {result && (
        <div className="tool-result">
          <div className="result-date">
            {result.isSpecial ? (
              <><span className="special-day">{result.month}</span>, {result.year} DR</>
            ) : (
              <>{result.month} {result.day}, {result.year} DR</>
            )}
          </div>
          {result.notable && <div className="result-note">{result.notable}</div>}
          {result.isSpecial && !result.notable && <div className="result-note">Festival Day</div>}
          <button className="btn btn-secondary btn-sm" onClick={useAsStartDate}>
            Use as Start Date
          </button>
        </div>
      )}
    </section>
  )
}

function DurationCalculator() {
  const [startDate, setStartDate] = useState({ month: 'Hammer', day: 1, year: 1492 })
  const [endDate, setEndDate] = useState({ month: 'Hammer', day: 1, year: 1492 })
  const [result, setResult] = useState(null)
  const [error, setError] = useState(null)

  const calculate = () => {
    setError(null)
    setResult(null)

    const startIsSpecial = isSpecialDay(startDate.month)
    const endIsSpecial = isSpecialDay(endDate.month)

    if (!startIsSpecial && (startDate.day < 1 || startDate.day > 30)) {
      setError('Start day must be between 1 and 30')
      return
    }

    if (!endIsSpecial && (endDate.day < 1 || endDate.day > 30)) {
      setError('End day must be between 1 and 30')
      return
    }

    if (startDate.month === 'Shieldmeet' && !isLeapYear(startDate.year)) {
      setError(`${startDate.year} DR is not a leap year — Shieldmeet does not occur`)
      return
    }

    if (endDate.month === 'Shieldmeet' && !isLeapYear(endDate.year)) {
      setError(`${endDate.year} DR is not a leap year — Shieldmeet does not occur`)
      return
    }

    const days = daysBetween(
      startDate.month, startDate.day, startDate.year,
      endDate.month, endDate.day, endDate.year
    )

    // Calculate breakdown
    const absDays = Math.abs(days)
    const years = Math.floor(absDays / 365)
    const tendays = Math.floor((absDays % 365) / 10)
    const remainingDays = absDays % 10

    const breakdown = []
    if (years > 0) breakdown.push(`${years} year${years !== 1 ? 's' : ''}`)
    if (tendays > 0) breakdown.push(`${tendays} tenday${tendays !== 1 ? 's' : ''}`)
    if (remainingDays > 0 || breakdown.length === 0) {
      breakdown.push(`${remainingDays} day${remainingDays !== 1 ? 's' : ''}`)
    }

    setResult({
      days: absDays,
      breakdown: breakdown.join(', '),
      isNegative: days < 0
    })
  }

  const swapDates = () => {
    const temp = startDate
    setStartDate(endDate)
    setEndDate(temp)
    setResult(null)
  }

  return (
    <section className="tool-card">
      <h2>Duration Calculator</h2>
      <p className="tool-description">Calculate the number of days between two Harptos dates</p>

      <div className="date-section">
        <h3>Start Date</h3>
        <HarptosDateInput
          prefix="dur-start"
          month={startDate.month}
          day={startDate.day}
          year={startDate.year}
          onChange={setStartDate}
        />
      </div>

      <div className="date-section">
        <h3>End Date</h3>
        <HarptosDateInput
          prefix="dur-end"
          month={endDate.month}
          day={endDate.day}
          year={endDate.year}
          onChange={setEndDate}
        />
      </div>

      <button className="btn btn-primary" onClick={calculate}>
        Calculate Duration
      </button>

      {error && <div className="tool-error">{error}</div>}

      {result && (
        <div className="tool-result">
          <div className="result-days">{result.days.toLocaleString()}</div>
          <div className="result-label">days</div>
          <div className="result-breakdown">
            {result.breakdown}
            {result.isNegative && <span className="negative-note"> (end date is before start date)</span>}
          </div>
          <button className="btn btn-secondary btn-sm" onClick={swapDates}>
            Swap Dates
          </button>
        </div>
      )}
    </section>
  )
}

export default function ToolsPage() {
  return (
    <div className="tools-page">
      <div className="container">
        <header className="tools-header">
          <h1>Tools</h1>
          <p className="tools-subtitle">Calendar of Harptos utilities for the Forgotten Realms</p>
        </header>

        <div className="tools-grid">
          <DateCalculator />
          <DurationCalculator />
          <Link to="/tools/cipher" className="tool-card tool-link">
            <h2>Cipher Tool</h2>
            <p className="tool-description">Encode and decode secret messages using ciphers your characters have learned</p>
            <span className="tool-link-arrow">→</span>
          </Link>
        </div>

        <footer className="tools-footer">
          <a
            href="https://forgottenrealms.fandom.com/wiki/Calendar_of_Harptos"
            target="_blank"
            rel="noopener noreferrer"
          >
            Calendar info from Forgotten Realms Wiki
          </a>
        </footer>
      </div>
    </div>
  )
}
